import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Card,
  CardHeader,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Link,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, UploadFile as UploadFileIcon, Download as DownloadIcon, Close as CloseIcon } from '@mui/icons-material';
import api from '../utils/api';
import { useNotification } from '../contexts/NotificationContext';
import { parseISO, isValid, format } from 'date-fns';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });
  const { showNotification } = useNotification();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [filter, setFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/');
      console.log("Raw User Data from API:", response.data);

      const usersWithId = response.data.map(u => ({
        ...u,
        id: u.email,
      }));

      setUsers(usersWithId);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    try {
      await api.delete(`/users/${userToDelete.email}`);
      // Refresh user list
      fetchUsers();
      // Show success message or notification here if needed
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setUploadError('');
    setSelectedFile(null);
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
    }
  };

  const handleCloseModal = () => {
    if (isUploading) return;
    setIsModalOpen(false);
  };

  const handleFileChange = (event) => {
    setUploadError('');
    if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0];
        const allowedExtensions = /(\.xlsx|\.xls)$/i;
        if (!allowedExtensions.exec(file.name)) {
            setUploadError('Invalid file type. Please upload an Excel file (.xlsx or .xls).');
            setSelectedFile(null);
            event.target.value = '';
            return;
        }
        setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setUploadError('Please select an Excel file first.');
      return;
    }
    setIsUploading(true);
    setUploadError('');
    const reader = new FileReader();

    reader.onload = async (evt) => {
       try {
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false, defval: '' });
        console.log("Parsed Excel Data (JSON):", jsonData);

        const usersToUpload = jsonData.map(row => ({
            email: String(row.email || '').trim(),
            subscription_tier: String(row.subscription_tier || '').trim() || null,
            subscription_status: String(row.subscription_status || '').trim() || null,
        })).filter(user => user.email);

        if (usersToUpload.length === 0) {
            setUploadError('No rows with a valid email address found in the file.');
            setIsUploading(false);
            return;
        }
        console.log("Valid users to upload (email only):", usersToUpload);

        try {
          const response = await api.post('/users/bulk', usersToUpload);
          console.log("Bulk Upload API Response:", response.data);
          showNotification(`Processed ${jsonData.length} rows from file. Added: ${response.data?.added_count ?? 0}. Skipped: ${response.data?.skipped_count ?? 0}.`, 'success');
          handleCloseModal();
          fetchUsers();
        } catch (apiError) {
           console.error("Bulk upload API error:", apiError);
           const message = apiError.response?.data?.detail || apiError.message || 'Server error during bulk upload.';
           setUploadError(`Upload failed: ${message}`);
           showNotification(`Bulk upload failed. ${message}`, 'error');
        } finally {
          setIsUploading(false);
        }
       } catch (parseError) {
         console.error("Excel Parsing Error:", parseError);
         setUploadError(`Failed to read or parse Excel file: ${parseError.message}`);
         setIsUploading(false);
       }
    };
    reader.onerror = (error) => {
        console.error("File Reading Error:", error);
        setUploadError(`Failed to read file: ${error}`);
        setIsUploading(false);
    };
    reader.readAsBinaryString(selectedFile);
  };

  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 200
    },
    {
      field: 'username',
      headerName: 'Username',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'subscription_tier',
      headerName: 'Tier',
      width: 120,
      renderCell: (params) => (params.value ? <Chip label={params.value} size="small" color={params.value === 'premium' ? 'secondary' : 'primary'} /> : null)
    },
    {
      field: 'subscription_status',
      headerName: 'Status',
      width: 120,
      type: 'singleSelect',
      valueOptions: ['active', 'trialing', 'past_due', 'cancelled'],
      renderCell: (params) => (params.value ? <Chip label={params.value} size="small" color={params.value === 'active' ? 'success' : 'warning'} /> : null),
    },
    {
       field: 'created_at',
       headerName: 'Created Date',
       width: 180,
       renderCell: (params) => {
            const rawValue = params.value;
            console.log(`renderCell for created_at (Row ID: ${params.id}): Received value:`, rawValue);

            if (rawValue == null || typeof rawValue !== 'string' || rawValue.trim() === '') {
                return 'N/A';
            }

            try {
                const date = parseISO(rawValue.trim());
                if (isValid(date)) {
                    return format(date, 'Pp');
                } else {
                    console.warn(`renderCell (Row ID: ${params.id}): Invalid date after parsing. Original:`, rawValue);
                    return 'Invalid Date';
                }
            } catch (error) {
                console.error(`renderCell (Row ID: ${params.id}): Error processing date value. Original:`, rawValue, error);
                return 'Error';
            }
       }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box>
          {/* Edit Button (uncomment if/when needed)
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          */}
          <Tooltip title="Delete">
             <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(filter.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
           <Button variant="contained" color="primary" startIcon={<AddIcon />} component={RouterLink} to="/users/add">
             Add User
           </Button>
           <Button
              variant="outlined"
              onClick={handleOpenModal}
              startIcon={<UploadFileIcon />}
            >
              Bulk Add Users
            </Button>
        </Box>
      </Box>
      <Card>
        <CardHeader title="All Users" />
        <Divider />
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
             rows={users}
             columns={columns}
             loading={loading}
             paginationModel={paginationModel}
             onPaginationModelChange={setPaginationModel}
             pageSizeOptions={[10, 25, 50, 100]}
             pagination
             disableSelectionOnClick
             sx={{ border: 0 }}
          />
        </Box>
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user "{userToDelete?.username || userToDelete?.email}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
           Bulk Add Substack Users
           <IconButton aria-label="close" onClick={handleCloseModal} disabled={isUploading} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
              <CloseIcon />
           </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
             <DialogContentText>
               Upload an Excel file (.xlsx or .xls) with user emails. Usernames will be automatically set to match the emails.
             </DialogContentText>
             <Box sx={{ border: '1px dashed', borderColor: 'divider', p: 2, borderRadius: 1 }}>
               <Typography variant="subtitle2" gutterBottom>Instructions:</Typography>
               <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                 <li>File must be an Excel spreadsheet (.xlsx or .xls).</li>
                 <li>The first row must contain the header: <code>email</code>.</li>
                 <li>Optional headers: <code>subscription_tier</code>, <code>subscription_status</code>.</li>
                 <li><code>email</code> is required for every row.</li>
                 <li>Valid tiers: <code>free</code>, <code>premium</code> (default: free).</li>
                 <li>Valid statuses: <code>active</code>, <code>trialing</code>, <code>past_due</code>, <code>cancelled</code> (default: trialing).</li>
                 <li>Rows with missing/invalid email or invalid optional values will be skipped.</li>
               </Typography>
             </Box>
             <Link href="/sample-substack-users.xlsx" download variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
               <DownloadIcon sx={{ mr: 0.5 }} fontSize="small"/>
               Download Sample Excel Template (Email only required)
             </Link>
             <Button variant="outlined" component="label" disabled={isUploading}>
               {selectedFile ? `Selected: ${selectedFile.name}` : "Choose Excel File"}
               <input type="file" hidden accept=".xlsx, .xls" onChange={handleFileChange} ref={fileInputRef}/>
             </Button>
             {uploadError && (<Alert severity="error" sx={{ mt: 1 }}>{uploadError}</Alert>)}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} disabled={isUploading} color="inherit">Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!selectedFile || isUploading} startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : null}>
            {isUploading ? 'Uploading...' : 'Upload & Add Users'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;